import { Layers, Split } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

export type SplitGroupSummary = { label: string; count: number };

/**
 * Visas i "Granska och justera" när AI:n sett flera olika saker på bilderna.
 * Vid snarlika föremål ges bara en neutral notis – uppdelning föreslås enbart
 * när sakerna hör till helt olika kategorier.
 */
export function SplitSuggestion({
  cohesion,
  groups,
  onSplit,
  onKeep,
  quotaNote,
  readOnly = false,
}: {
  cohesion: "same" | "related" | "mixed";
  groups: SplitGroupSummary[];
  onSplit?: () => void;
  onKeep?: () => void;
  quotaNote?: string | null;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  if (cohesion === "same" || groups.length < 2) return null;

  const labels = groups.map((g) => g.label).filter(Boolean);
  const listed =
    labels.length > 1
      ? `${labels.slice(0, -1).join(", ")}${t("sell.split.join")}${labels[labels.length - 1]}`
      : (labels[0] ?? "");

  if (cohesion === "related") {
    return (
      <div className="flex items-start gap-3 rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-ocean-100">
        <Layers className="mt-0.5 h-4 w-4 shrink-0 text-ocean-300" />
        <p>
          {t("sell.split.related", { items: listed ? `: ${listed}` : "" })}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-ocean-300/40 bg-ocean-700/30 p-4">
      <div className="flex items-start gap-3">
        <Split className="mt-0.5 h-5 w-5 shrink-0 text-ocean-300" />
        <div className="flex-1">
          <p className="font-heading font-semibold text-white">{t("sell.split.title")}</p>
          <p className="mt-1 text-sm text-ocean-100">
            {t("sell.split.mixed", { items: listed || t("sell.split.several") })}
          </p>
          {quotaNote && <p className="mt-2 text-xs text-ocean-100/80">{quotaNote}</p>}
          {!readOnly && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onSplit}
                className="inline-flex items-center gap-2 rounded-lg bg-ocean-300 px-4 py-2 text-sm font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90"
              >
                <Split className="h-4 w-4" />
                {t("sell.split.split", { count: groups.length })}
              </button>
              <button
                type="button"
                onClick={onKeep}
                className="inline-flex items-center rounded-lg border border-ocean-300/30 bg-ocean-700/30 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-ocean-700/50"
              >
                {t("sell.split.keep")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
